import { getFunder, getFunders, saveFunder } from '@/api/funders';
import { Funder, FunderState } from '@/types/funder';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

type FunderGetter = GetterTree<FunderState, RootState>;

const state: FunderState = {
  funders: {
    data: [],
    current_page: 1,
    first_page_url: 'http://cms.test/api/funders?page=1',
    from: 1,
    last_page: 1,
    last_page_url: null,
    next_page_url: null,
    path: 'http://cms.test/api/funders',
    per_page: 50,
    prev_page_url: null,
    to: 50,
    total: 0,
  },
  selected: {
    id: null,
    name: null,
    logo: null,
    published_products_count: 0,
    unpublished_products_count: 0,
    tier_one_products_count: 0,
    created_at: null,
    updated_at: null,
    meta: null,
  },
  filters: {
    enabled: null,
    tier: null,
  },
};

const getters: FunderGetter = {
  funders: state => state.funders.data,
  funder: state => state.selected,
  tierFilter: state => state.filters.tier,
  enabledFilter: state => state.filters.enabled,
  currentFunderPage: state => state.funders.current_page,
  totalFunderPages: state => state.funders.last_page,
};

const actions: ActionTree<FunderState, RootState> = {
  async getFunders({ commit, state }, { page = 1 }): Promise<void> {
    commit('setFunders', await getFunders(page, state.filters));
  },

  async getFunder({ commit }, funder: Funder) {
    commit('setSelectedFunder', funder);
  },

  async getFunderBySlug({ commit, state }, slug: string) {
    const index = state.funders.data.findIndex(
      (funder: Funder) => funder.meta!.slug === slug,
    );
    if (index >= 0) {
      commit('setSelectedFunder', state.funders.data[index]);
    } else {
      commit('setSelectedFunder', await getFunder(slug));
    }
  },

  async updateFunder({ state }) {
    await saveFunder(state.selected);
  },
};

const mutations: MutationTree<FunderState> = {
  setFunders(state: FunderState, funderData: FunderState): void {
    Object.assign(state.funders, funderData);
  },

  setSelectedFunder(state: FunderState, funder: Funder): void {
    Object.assign(state.selected, funder);
  },

  setFunderDescription(funderState: FunderState, description: string): void {
    funderState.selected.meta!.description = description;
  },

  setTierFilter(funderState: FunderState, tier: string | null): void {
    funderState.filters.tier = tier;
  },
  setEnabledFilter(funderState: FunderState, enabled: boolean | null): void {
    funderState.filters.enabled = enabled;
  },
  setFunderNoIndex(funderState: FunderState, noindex: boolean): void {
    funderState.selected.meta!.noindex = noindex;
  },
  setFunderMetaTitle(funderState: FunderState, title: string): void {
    funderState.selected.meta!.meta_title = title;
  },
  setFunderMetaDescription(
    funderState: FunderState,
    description: string,
  ): void {
    funderState.selected.meta!.meta_description = description;
  },
  setFunderCanonicalUrl(funderState: FunderState, canonicalUrl: string): void {
    funderState.selected.meta!.canonical_url = canonicalUrl;
  },
  setFunderTwitterTitle(funderState: FunderState, title: string): void {
    funderState.selected.meta!.twitter_title = title;
  },
  setFunderTwitterDescription(
    funderState: FunderState,
    description: string,
  ): void {
    funderState.selected.meta!.twitter_description = description;
  },
  setFunderOpenGraphTitle(funderState: FunderState, title: string): void {
    funderState.selected.meta!.open_graph_title = title;
  },
  setFunderOpenGraphDescription(
    funderState: FunderState,
    description: string,
  ): void {
    funderState.selected.meta!.open_graph_description = description;
  },
};

const content: Module<FunderState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default content;
